import { useCallback, useState } from "react";
import { useApi } from "../apiCall/useApi";
import Joi from "joi";
import { Urls } from "../../../utils/constant";
import { size } from "lodash";
import { useSnackbar } from "notistack";
import Api from "../../../utils/api.utils";
import {
  hidePageLoader,
  showPageLoader,
} from "../../reducers/Common/PageLoader/PageLoader.reducer";
import { useDispatch, useSelector } from "react-redux";
import useFullPageLoader from "../Common/PageLoader/useFullPageLoader";
import {
  setDoctorComboList,
  setUserMasterData,
} from "../../reducers/User_Master/user_MasterForm.reducer";
import { getDoctorList } from "../../selectors/User_Master/user_MasterForm.selector";

export const UserMasterEntrySchema = Joi.object({
  doctor_name: Joi.string().required().messages({
    "string.empty": "Doctor Name is required.",
  }),
  user_name: Joi.string().required().messages({
    "string.empty": "User Name is required.",
  }),
  password:
    size() > 0
      ? Joi.string().optional().allow("")
      : Joi.string()
          .pattern(
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/
          )
          .required()
          .messages({
            "string.empty": "Password is required.",
            "string.pattern.base":
              "Password must be 8 characters long and should have at least one uppercase and lowercase letter, one number and any special character.",
          }),
  name: Joi.string().required().messages({
    "string.empty": "Name is required.",
  }),
  email_id: Joi.string()
    .email({ tlds: { allow: false } })
    .required()
    .messages({
      "string.empty": "Email is required.",
      "string.email": "Enter a valid email address.",
    }),
  mobile_no: Joi.string().min(10).required().messages({
    "string.empty": "Mobile number is required.",
    "string.min": "Please enter a valid contact number.",
  }),
});

export function useUserMasterForm({
  existingStatus,
  existingDoctorName,
  existingImageUrl,
}) {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { apiPost } = useApi();
  const [loading, setLoading] = useState(false);
  const [loader, showLoader, hideLoader] = useFullPageLoader();
  const [status, setStatus] = useState(existingStatus === 2 ? 0 : 1);
  const [DoctorId, setDoctorId] = useState(existingDoctorName);
  const doctorList = useSelector(getDoctorList);
  const [Image, setImage] = useState(existingImageUrl);

  const handleFileChange = useCallback((img) => {
    setImage(img);
  }, []);

  const UserMasterEntry = useCallback(
    async ({ name, user_name, password, email_id, mobile_no }) => {
      const status_Val = status === false ? 2 : status === 0 ? 2 : 1;
      const image_url = [];
      if (loading) return;
      setLoading(true);
      try {
        dispatch(showPageLoader());

        if (size(Image?.name) > 0) {
          const form = new FormData();
          form.append("file", Image);

          const image_response = await Api.post2(
            {
              url: Urls.imageAdd(),
              data: form,
              headers: {
                Accept: "application/json",
                "Content-Type": "multipart/form-data",
              },
            },
            []
          );

          image_url.push(image_response?.data[0]);
        }

        // const formattedReportDate = format(ReportDate, 'yyyy-MM-dd');
        const { data } = await apiPost({
          url: Urls.UserMasterAdd(),
          data: {
            name: name,
            doctor_id: DoctorId,
            image_url: image_url[0]?.url,
            user_name: user_name,
            password: password,
            email_id: email_id,
            mobile_no: mobile_no,
            status: status_Val,
            insert_by: "",
          },
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        });
        if (size(data.id) === 36) {
          enqueueSnackbar("User Data Added Successfully.", {
            variant: "success",
          });
          return true;
        }

        setLoading(false);
        dispatch(hidePageLoader());
      } catch (err) {
        dispatch(hidePageLoader());
        console.log(err);
        setLoading(false);
        return false;
      }
    },
    [status, loading, DoctorId, dispatch, apiPost, enqueueSnackbar, Image]
  );

  const UserMasterUpdate = useCallback(
    async ({ id, name, user_name, password, email_id, mobile_no }) => {
      if (loading) return;
      setLoading(true);
      const status_Val = status === false ? 2 : status === 0 ? 2 : 1;
      const image_url = [];

      try {
        dispatch(showPageLoader());
        if (size(Image?.name) > 0) {
          const form = new FormData();
          form.append("file", Image);

          const image_response = await Api.post2(
            {
              url: Urls.imageAdd(),
              data: form,
              headers: {
                Accept: "application/json",
                "Content-Type": "multipart/form-data",
              },
            },
            []
          );

          image_url.push(image_response?.data[0]);
        }

        const { data } = await apiPost({
          url: Urls.UserMasterUpdate(),
          data: {
            id: id,
            name: name,
            doctor_id: DoctorId,
            image_url:
              size(image_url[0]?.url) > 0 ? image_url[0]?.url : Image?.url,
            user_name: user_name,
            password: password,
            email_id: email_id,
            mobile_no: mobile_no,
            status: status_Val,
            update_by: "",
          },
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        });

        if (size(data.id) === 36) {
          enqueueSnackbar("User Data Updated Successfully.", {
            variant: "success",
          });
          return true;
        }

        setLoading(false);
        dispatch(hidePageLoader());
      } catch (err) {
        dispatch(hidePageLoader());
        console.log(err);
        setLoading(false);
        return false;
      }
    },
    [loading, status, DoctorId, Image, dispatch, apiPost, enqueueSnackbar]
  );

  const fetchDoctorComboList = useCallback(async () => {
    try {
      const { data } = await apiPost({
        url: Urls.getDoctorCombo(),
        data: {},
      });
      // setting list to reducer

      dispatch(setDoctorComboList(data.data));
    } catch (err) {
      console.log(err);
    }
  }, [apiPost, dispatch]);

  const findDoctorFromLabel = useCallback(
    (value) => {
      const selectedDoctorId = value
        ? doctorList.find((item) => item.label === value)?.id
        : null;

      setDoctorId(selectedDoctorId);
      // fetchWeekDayList(selectedDoctorId);
      return selectedDoctorId;
    },
    [doctorList]
  );

  const getUserMasterbyId = useCallback(
    async (id) => {
      if (loading) return;
      setLoading(true);
      try {
        dispatch(showPageLoader());

        const { data } = await apiPost({
          url: Urls.getUserMasterDatabyId(),
          data: {
            id: id,
          },
        });

        dispatch(setUserMasterData(data));
        setLoading(false);
        dispatch(hidePageLoader());
      } catch (err) {
        dispatch(hidePageLoader());
        console.log(err);
        setLoading(false);
        return false;
      }
    },
    [loading, dispatch, apiPost]
  );

  const handleStatusComboChange = useCallback((event) => {
    if (event.target.checked === 1) {
      setStatus(event.target.checked);
    } else {
      setStatus(event.target.checked);
    }
  }, []);

  return {
    loader,
    loading,
    status,
    DoctorId,
    UserMasterEntrySchema,
    handleStatusComboChange,
    UserMasterEntry,
    UserMasterUpdate,
    getUserMasterbyId,
    fetchDoctorComboList,
    findDoctorFromLabel,
    handleFileChange,
    // UserMasterDelete,
    // handleDeleteId,
  };
}
